import { ExpressionSpecification, Map } from "maplibre-gl";
import { FeatureCollection, Geometry } from "geojson";

import { DatasetStyle } from "@/types/dataset";
import { FeatureDataConfig } from "@/types/map";
import { generateOrdinalColorExpression } from "@/utils/map";

import { addLabelLayer } from "./label";
import { addRankLabelLayer } from "./rank";

/**
 * Creates and configures a GeoJSON source for linestring data
 * @param map - MapLibre map instance
 * @param sourceId - Unique source identifier
 * @param featureCollection - GeoJSON FeatureCollection with LineString/MultiLineString geometries
 */
export const addLineStringSource = (
  map: Map | null,
  sourceId: string,
  featureCollection: FeatureCollection
): void => {
  if (!map) return;

  const parsedLineStringData: FeatureCollection<Geometry> = {
    ...featureCollection,
    features: featureCollection.features.map((feature, index) => ({
      ...feature,
      properties: {
        ...feature.properties,
        unique_id: feature.properties?.unique_id || index
      }
    }))
  };

  map.addSource(sourceId, {
    type: "geojson",
    data: parsedLineStringData,
    promoteId: "unique_id"
  });
};

/**
 * Creates line layers with ordinal color mapping (color by category)
 * @param _isNewUIDatasetConfigEnabled - Flag for new UI dataset config
 * @param _isRankingLabelEnabled - Flag for ranking label
 * @param map - MapLibre map instance
 * @param layer - Layer configuration object
 * @param style - Dataset style configuration
 * @param category - Category attribute name for coloring
 */
export const addLineStringOrdinalLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: Map | null,
  layer: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string
): void => {
  if (!map) return;

  const sourceId = layer.id;
  const layerId = `${sourceId}-line-stroke`;

  if (map.getLayer(layerId)) {
    map.removeLayer(layerId);
  }

  const colorExpression = generateOrdinalColorExpression(
    style.colorRanges,
    style.colorFamily,
    category,
    style.colorSteps
  );

  const lineWidth = style.stroke?.width ?? style.weight ?? 2;
  const lineOpacity = style.stroke?.opacity ?? style.opacity ?? 1;

  map.addLayer({
    id: layerId,
    type: "line",
    source: sourceId,
    layout: {
      "line-join": "round",
      "line-cap": "round",
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": colorExpression,
      "line-width": lineWidth,
      "line-opacity": lineOpacity
    }
  });

  addLabelLayer(isNewUIDatasetConfigEnabled, map, layer, style);
  addRankLabelLayer(isRankingLabelEnabled, map, layer, style);
};

/**
 * Creates line layers with quantile color mapping (color by value range)
 * @param map - MapLibre map instance
 * @param sourceId - Source identifier
 * @param colorRanges - Array of color ranges with min/max values
 * @param selectedCategory - Attribute name to color by
 * @param opacity - Layer opacity
 * @param width - Line width
 */
export const addLineStringQuantileLayers = (
  isNewUIDatasetConfigEnabled: boolean,
  isRankingLabelEnabled: boolean,
  map: Map | null,
  layer: Omit<FeatureDataConfig, "data"> & { id: string },
  style: DatasetStyle,
  category: string
): void => {
  if (!map) return;

  const layerId = `${layer.id}-line-stroke`;

  if (map.getLayer(layerId)) {
    map.removeLayer(layerId);
  }

  // Sort ranges by min value to ensure correct step expression
  const sortedRanges = [...style.colorRanges].sort((a, b) => {
    return (a.min ?? 0) - (b.min ?? 0);
  });

  // Create step expression
  // ["step", ["get", property], defaultColor, stop1, color1, stop2, color2, ...]
  const stepExpression: ExpressionSpecification = [
    "step",
    ["get", category],
    sortedRanges[0]?.color || "#ccc", // Default color (below first min)
    ...sortedRanges.flatMap((range) => {
      if (range.min !== undefined && range.min !== null) {
        return [range.min, range.color];
      }
      return [];
    })
  ];

  map.addLayer({
    id: layerId,
    type: "line",
    source: layer.id,
    layout: {
      "line-join": "round",
      "line-cap": "round",
      visibility: style?.visibility || "visible"
    },
    paint: {
      "line-color": stepExpression,
      "line-width": style.stroke?.width ?? style.weight ?? 2,
      "line-opacity": style.stroke?.opacity ?? style.opacity ?? 1
    }
  });

  addLabelLayer(isNewUIDatasetConfigEnabled, map, layer, style);
  addRankLabelLayer(isRankingLabelEnabled, map, layer, style);
};
